import {
  Box,
  FormControlLabel,
  makeStyles,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import { QuotationCoverage } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { colors, DisplayItem } from 'design-system';
import React, { useEffect, useState } from 'react';
import { packageOptionsDescription } from 'src/functions/individualOptionHelpers';
import { REQUEST_PRICING } from 'src/store/quotation/requestIdentifiers';

const iconDiameter = 24;

const useStyles = makeStyles({
  container: {
    display: 'block',
    padding: '4px 0',
    margin: 0,
    width: '100%',
  },
  baseMark: {
    display: 'inline-block',
    width: iconDiameter,
    height: iconDiameter,
    marginRight: 8,
    borderRadius: iconDiameter / 2.0,
  },
  baseMarkContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: iconDiameter,
    height: iconDiameter,
  },
  markUnchecked: {
    backgroundColor: 'transparent',
    border: `1px solid ${colors.bright_turquoise}`,
  },
  markChecked: {
    backgroundColor: colors.bright_turquoise,
  },
  markDisabledChecked: {
    backgroundColor: colors.light_silver,
  },
  radioOption: {
    width: '100%',
    '& .MuiFormControlLabel-label': {
      width: '100%',
      marginLeft: '8px',
    },
  },
});

type SelectionCardProps = {
  /** The card title (basically, the call to action) */
  title: React.ReactNode;
  /** The options to display */
  options: DisplayItem[];
  /** Called when a new option is selected */
  onUpdateCurrentOption: (newOption: DisplayItem) => void;
  /** Additional content */
  additionalContent?: React.ReactNode;
  inputId?: string;
  endorsement?: QuotationCoverage;
  currentSelectedPlan: string;
  typeOfMoney: string;
};

const SelectionCard = ({
  title,
  options,
  onUpdateCurrentOption,
  additionalContent,
  inputId,
  endorsement,
  currentSelectedPlan,
  typeOfMoney,
}: SelectionCardProps): JSX.Element => {
  const [optionSelected, setOptionSelected] = useState(
    endorsement?.coverageId || '',
  );

  useEffect(() => {
    setOptionSelected(endorsement?.coverageId || '');
  }, [currentSelectedPlan, endorsement?.coverageId]);

  const classes = useStyles();
  const requestingQuotationState = useNetworkObserver(REQUEST_PRICING);
  const packageOptions = packageOptionsDescription[typeOfMoney as '₡' | '$'];

  return (
    <Box data-testid="radio-group">
      {title}
      <div style={{ height: 5 }} />
      {currentSelectedPlan === packageOptions.flexible && (
        <RadioGroup
          id={inputId}
          aria-labelledby="package-options-group"
          name="package-options"
          value={optionSelected}
          onChange={(event) => {
            const selectedOption = options.find(
              (opt) => opt.Value === event.target.value,
            );
            if (selectedOption) {
              onUpdateCurrentOption(selectedOption);
              setOptionSelected(selectedOption.Value);
            }
          }}
        >
          {options.map((option: DisplayItem) => (
            <FormControlLabel
              key={option.Value}
              value={option.Value}
              data-testid={option.Value}
              control={
                <Radio
                  disabled={requestingQuotationState === 'requesting'}
                  color="primary"
                  style={{
                    padding: '8px',
                  }}
                />
              }
              label={
                typeof option.Text === 'string' ? (
                  <Typography variant="caption">{option.Text}</Typography>
                ) : (
                  option.Text
                )
              }
              className={classes.radioOption}
            />
          ))}
        </RadioGroup>
      )}
      {currentSelectedPlan !== packageOptions.flexible &&
        options.find(
          (option: DisplayItem): boolean =>
            option.Value === endorsement?.coverageId,
        )?.Text}
      {additionalContent}
    </Box>
  );
};

export default SelectionCard;
